import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import toast from 'react-hot-toast';

/**
 * ADMIN consignor-settlement console for ONE sale (AdminRoute-gated).
 *
 * Reads by ITERATING consignors (not a collectionGroup query): list consignors, then read
 * each consignors/{cid}/settlements subcollection and keep the docs for this auctionId. This
 * uses the EXISTING Phase-1 nested rule (settlements read/write allowed for isPaymentStaff) —
 * no collectionGroup index, no new rule.
 *
 * Mark-as-paid writes paymentInfo.{method,paidAt,checkNumber|transactionId} + status:'paid'
 * onto the settlement doc. DRAFT settlements are provisional (mid-sale) and are NOT payable.
 */

const toDate = (v: any): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v?.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};
const fmtMoney = (n?: number) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (v: any) => { const d = toDate(v); return d ? d.toLocaleDateString() : '—'; };

interface Row {
  cid: string;
  sid: string;
  consignorName: string;
  consignorNumber: string;
  status: 'draft' | 'final' | 'paid' | 'unknown';
  net: number;
  itemsSold: number;
  itemsUnsold: number;
  generatedAt: any;
  paymentDueDate: any;
  paidAt: any;
  paymentMethod: string;
}

const AdminSaleSettlements: React.FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  const [rows, setRows] = useState<Row[]>([]);
  const [saleTitle, setSaleTitle] = useState('');
  const [loading, setLoading] = useState(true);

  // Mark-as-paid modal state
  const [paying, setPaying] = useState<Row | null>(null);
  const [method, setMethod] = useState<'check' | 'ach' | 'wire'>('check');
  const [checkNumber, setCheckNumber] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [paidDate, setPaidDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!auctionId) return;
      try {
        const [aSnap, consignorSnap] = await Promise.all([
          getDoc(doc(db, 'auctions', auctionId)),
          getDocs(collection(db, 'consignors')),
        ]);
        if (!active) return;
        setSaleTitle((aSnap.exists() && (aSnap.data() as any).title) || auctionId);

        const out: Row[] = [];
        for (const cDoc of consignorSnap.docs) {
          const c: any = cDoc.data();
          const setSnap = await getDocs(collection(db, 'consignors', cDoc.id, 'settlements'));
          setSnap.forEach((sDoc) => {
            const s: any = sDoc.data();
            if (s.auctionId !== auctionId) return; // this sale only (client-side filter)
            out.push({
              cid: cDoc.id,
              sid: sDoc.id,
              consignorName: c.name || '(unnamed)',
              consignorNumber: c.consignorNumber || c.number || c.legacyCode || '—',
              status: (s.status as Row['status']) || 'unknown',
              net: Number(s?.totals?.netPayment ?? s?.totals?.consignorPayout ?? 0),
              itemsSold: Number(s?.totals?.itemsSold ?? 0),
              itemsUnsold: Array.isArray(s?.unsoldItems) ? s.unsoldItems.length : 0,
              generatedAt: s.generatedAt,
              paymentDueDate: s.paymentDueDate,
              paidAt: s?.paymentInfo?.paidAt ?? null,
              paymentMethod: s?.paymentInfo?.method || '',
            });
          });
        }
        out.sort((a, b) => a.consignorName.localeCompare(b.consignorName));
        if (active) setRows(out);
      } catch (err) {
        console.error('Error loading settlements:', err);
        toast.error('Failed to load settlements.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [auctionId]);

  const totals = useMemo(() => {
    let owed = 0, paid = 0, pending = 0;
    for (const r of rows) {
      owed += r.net;
      if (r.paidAt) paid += r.net; else if (r.status === 'final') pending += r.net;
    }
    return { owed, paid, pending };
  }, [rows]);

  const isPaid = (r: Row) => !!r.paidAt || r.status === 'paid';
  const isPayable = (r: Row) => r.status === 'final' && !isPaid(r);

  const openPay = (r: Row) => {
    setPaying(r);
    setMethod('check');
    setCheckNumber('');
    setTransactionId('');
    setPaidDate(new Date().toISOString().slice(0, 10));
  };

  const confirmPay = async () => {
    if (!paying) return;
    if (!window.confirm(
      `Mark ${paying.consignorName}'s settlement as PAID — ${fmtMoney(paying.net)} via ${method.toUpperCase()}?`,
    )) return;
    setSaving(true);
    try {
      const paymentInfo: any = { method, paidAt: new Date(paidDate).toISOString() };
      if (method === 'check' && checkNumber) paymentInfo.checkNumber = checkNumber;
      if ((method === 'ach' || method === 'wire') && transactionId) paymentInfo.transactionId = transactionId;

      await updateDoc(doc(db, 'consignors', paying.cid, 'settlements', paying.sid), {
        status: 'paid',
        paymentInfo,
        updatedAt: serverTimestamp(),
      });
      setRows((prev) => prev.map((r) =>
        r.sid === paying.sid && r.cid === paying.cid
          ? { ...r, status: 'paid', paidAt: paymentInfo.paidAt, paymentMethod: method }
          : r,
      ));
      toast.success(`Marked ${fmtMoney(paying.net)} paid.`);
      setPaying(null);
    } catch (err) {
      console.error('Mark-as-paid failed:', err);
      toast.error('Could not mark paid — admin/auctioneer only.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-gray-500">Loading settlements…</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Consignor Settlements</h1>
          <p className="text-gray-600">{saleTitle}</p>
        </div>
        <Link to="/admin/consignors" className="btn-secondary">All consignors</Link>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b bg-gray-50">
              <th className="py-2 px-3">Consignor</th>
              <th className="py-2 px-3">#</th>
              <th className="py-2 px-3 text-right">Net owed</th>
              <th className="py-2 px-3 text-right">Sold / Unsold</th>
              <th className="py-2 px-3">Generated</th>
              <th className="py-2 px-3">Due</th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.cid}_${r.sid}`} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-2 px-3 font-semibold text-gray-900">{r.consignorName}</td>
                <td className="py-2 px-3 font-mono text-xs text-gray-500">{r.consignorNumber}</td>
                <td className="py-2 px-3 text-right font-bold">{fmtMoney(r.net)}</td>
                <td className="py-2 px-3 text-right">{r.itemsSold} / {r.itemsUnsold}</td>
                <td className="py-2 px-3 text-gray-600">{fmtDate(r.generatedAt)}</td>
                <td className="py-2 px-3 text-gray-600">{fmtDate(r.paymentDueDate)}</td>
                <td className="py-2 px-3">
                  {isPaid(r) ? (
                    <span className="text-xs font-bold px-2 py-0.5 rounded bg-green-100 text-green-800">
                      PAID{r.paymentMethod ? ` · ${r.paymentMethod}` : ''}
                    </span>
                  ) : r.status === 'draft' ? (
                    <span className="text-xs font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800" title="Provisional mid-sale figure — not payable">
                      DRAFT
                    </span>
                  ) : r.status === 'final' ? (
                    <span className="text-xs font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800">PENDING</span>
                  ) : (
                    <span className="text-xs font-bold px-2 py-0.5 rounded bg-gray-100 text-gray-600">{r.status}</span>
                  )}
                </td>
                <td className="py-2 px-3 text-right">
                  <Link
                    to={`/admin/consignors/${r.cid}/settlements/${r.sid}`}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    View
                  </Link>
                  {isPayable(r) && (
                    <button
                      type="button"
                      onClick={() => openPay(r)}
                      className="text-green-700 hover:text-green-900 font-semibold"
                    >
                      Mark paid
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="py-6 text-center text-gray-500">No settlements for this sale yet.</td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 bg-gray-50 font-semibold">
                <td className="py-2 px-3" colSpan={2}>Sale totals</td>
                <td className="py-2 px-3 text-right">{fmtMoney(totals.owed)}</td>
                <td className="py-2 px-3" colSpan={3}></td>
                <td className="py-2 px-3 text-xs">
                  <span className="text-green-700">Paid {fmtMoney(totals.paid)}</span>
                  {' · '}
                  <span className="text-blue-700">Pending {fmtMoney(totals.pending)}</span>
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <p className="text-xs text-gray-500">
        Draft settlements are provisional mid-sale figures and can't be marked paid. Run the final
        settlement pass (post-close) to make a consignor payable.
      </p>

      {/* Mark-as-paid modal */}
      {paying && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-4">
            <div>
              <h2 className="text-lg font-bold">Mark settlement paid</h2>
              <p className="text-sm text-gray-600">
                {paying.consignorName} — <span className="font-bold">{fmtMoney(paying.net)}</span>
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Method</label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value as any)}
                className="input w-full"
              >
                <option value="check">Check</option>
                <option value="ach">ACH</option>
                <option value="wire">Wire</option>
              </select>
            </div>

            {method === 'check' ? (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Check number</label>
                <input className="input w-full" value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} placeholder="e.g. 1042" />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Transaction ID</label>
                <input className="input w-full" value={transactionId} onChange={(e) => setTransactionId(e.target.value)} placeholder="confirmation / trace #" />
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Paid on</label>
              <input type="date" className="input w-full" value={paidDate} onChange={(e) => setPaidDate(e.target.value)} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setPaying(null)} disabled={saving} className="btn-secondary">Cancel</button>
              <button type="button" onClick={confirmPay} disabled={saving} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 font-semibold">
                {saving ? 'Saving…' : `Mark ${fmtMoney(paying.net)} paid`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSaleSettlements;
